/**
 * TanStack Query cache keys.
 *
 * THE RULE: every key that names tenant data begins with `["org", organizationId]`.
 *
 * This is not tidiness. Two organizations' rows live in one browser cache, and if a key
 * does not carry the org id, switching org shows the previous org's rows out of the cache.
 * The server never leaked anything — but the customer is looking at another company's
 * sites, and no amount of explaining makes that fine.
 *
 * `orgScope()` throws on a missing id rather than quietly keying on `undefined`, which would
 * merge every organization into one cache bucket. A crash in a test beats a leak in prod.
 */

export type OrgId = string;

function orgScope(orgId: OrgId): readonly ["org", OrgId] {
  if (!orgId) {
    throw new Error(
      "Tenant-scoped query key built without an organization id. Every cache entry holding " +
        "tenant data must be scoped by org, or switching org will show the previous org's rows.",
    );
  }
  return ["org", orgId] as const;
}

/** Filters are part of the key, so two filtered views never overwrite each other. */
export type Filters = Record<string, string | number | boolean | undefined>;

/** Drop undefined values so `{status: undefined}` and `{}` are the same cache entry. */
function normalize(filters?: Filters): Filters {
  if (!filters) return {};
  const out: Filters = {};
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key];
    if (value !== undefined && value !== "") out[key] = value;
  }
  return out;
}

export const qk = {
  // ---- identity: global, NOT tenant data. Deliberately unscoped.
  me: () => ["me"] as const,
  myOrganizations: () => ["my-organizations"] as const,
  invitationByToken: (token: string) => ["invitation", token] as const,

  // ---- everything below is tenant data and is scoped by organization id.
  org: (orgId: OrgId) => orgScope(orgId),
  organization: (orgId: OrgId) => [...orgScope(orgId), "organization"] as const,

  regions: (orgId: OrgId, filters?: Filters) =>
    [...orgScope(orgId), "regions", normalize(filters)] as const,

  sites: (orgId: OrgId, filters?: Filters) =>
    [...orgScope(orgId), "sites", normalize(filters)] as const,
  site: (orgId: OrgId, siteId: string) => [...orgScope(orgId), "site", siteId] as const,
  siteTanks: (orgId: OrgId, siteId: string) =>
    [...orgScope(orgId), "site", siteId, "tanks"] as const,
  siteGenerators: (orgId: OrgId, siteId: string) =>
    [...orgScope(orgId), "site", siteId, "generators"] as const,

  // ---- live telemetry — the current-state polls and the trend charts behind them
  tank: (orgId: OrgId, tankId: string) => [...orgScope(orgId), "tank", tankId] as const,
  tankReadings: (orgId: OrgId, tankId: string, filters?: Filters) =>
    [...orgScope(orgId), "tank", tankId, "readings", normalize(filters)] as const,
  generator: (orgId: OrgId, generatorId: string) =>
    [...orgScope(orgId), "generator", generatorId] as const,
  generatorReadings: (orgId: OrgId, generatorId: string, filters?: Filters) =>
    [...orgScope(orgId), "generator", generatorId, "readings", normalize(filters)] as const,

  // ---- field boards — the list, one board, and the streams behind its detail page
  devices: (orgId: OrgId) => [...orgScope(orgId), "devices"] as const,
  device: (orgId: OrgId, deviceId: string) => [...orgScope(orgId), "device", deviceId] as const,
  deviceReadings: (orgId: OrgId, deviceId: string, filters?: Filters) =>
    [...orgScope(orgId), "device", deviceId, "readings", normalize(filters)] as const,
  deviceLogs: (orgId: OrgId, deviceId: string) =>
    [...orgScope(orgId), "device", deviceId, "logs"] as const,
  deviceCommands: (orgId: OrgId, deviceId: string) =>
    [...orgScope(orgId), "device", deviceId, "commands"] as const,
  deviceCommand: (orgId: OrgId, deviceId: string, commandId: string) =>
    [...orgScope(orgId), "device", deviceId, "command", commandId] as const,
  firmware: (orgId: OrgId) => [...orgScope(orgId), "firmware"] as const,

  refills: (orgId: OrgId, filters?: Filters) =>
    [...orgScope(orgId), "refills", normalize(filters)] as const,
  refill: (orgId: OrgId, refillId: string) => [...orgScope(orgId), "refill", refillId] as const,

  refillRequests: (orgId: OrgId, filters?: Filters) =>
    [...orgScope(orgId), "refill-requests", normalize(filters)] as const,
  refillRequest: (orgId: OrgId, requestId: string) =>
    [...orgScope(orgId), "refill-request", requestId] as const,

  reportSummary: (orgId: OrgId, filters?: Filters) =>
    [...orgScope(orgId), "report-summary", normalize(filters)] as const,
  reportExports: (orgId: OrgId) => [...orgScope(orgId), "report-exports"] as const,

  members: (orgId: OrgId) => [...orgScope(orgId), "members"] as const,
  invitations: (orgId: OrgId) => [...orgScope(orgId), "invitations"] as const,
  apiKeys: (orgId: OrgId) => [...orgScope(orgId), "api-keys"] as const,
} as const;
